if (!customElements.get("product-info")) {
  customElements.define(
    "product-info",
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector(".quantity__input");
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(
          new CustomEvent("product-info:loaded", { bubbles: true })
        );
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector(".product-form__quantity");
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html
            .querySelectorAll(".scroll-trigger")
            .forEach((element) =>
              element.classList.add("scroll-trigger--cancel")
            )
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({
        data: { event, target, selectedOptionValues },
      }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl =
          target.dataset.productUrl ||
          this.pendingRequestUrl ||
          this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage =
          this.dataset.updateUrl === "true" && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            productUrl,
            selectedOptionValues,
            shouldFetchFullPage
          ),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage
            ? "product-info[id^='MainProduct']"
            : "product-info";
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector("head title").innerHTML =
              html.querySelector("head title").innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector("main"),
              html.querySelector("main"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector("product-info"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === "AbortError") {
              console.log("Fetch aborted by user");
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector(
          "variant-selects [data-selected-variant]"
        )?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(
        url,
        optionValues,
        shouldFetchFullPage = false
      ) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(",")}`);
        }

        return `${url}?${params.join("&")}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector("variant-selects");
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(
            this.variantSelectors,
            variantSelects,
            this.preProcessHtmlCallbacks
          );
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (
            id,
            shouldHide = (source) => false
          ) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(
              `#${id}-${this.dataset.section}`
            );
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle("hidden", shouldHide(source));
            }
          };

          updateSourceFromDestination("price");
          updateSourceFromDestination("Sku", ({ classList }) =>
            classList.contains("hidden")
          );
          updateSourceFromDestination(
            "Inventory",
            ({ innerText }) => innerText === ""
          );
          updateSourceFromDestination("Volume");
          updateSourceFromDestination("Price-Per-Item", ({ classList }) =>
            classList.contains("hidden")
          );

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(
            `#Quantity-Rules-${this.dataset.section}`
          )?.classList.remove("hidden");
          this.querySelector(
            `#Volume-Note-${this.dataset.section}`
          )?.classList.remove("hidden");

          this.productForm?.toggleSubmitButton(
            html
              .getElementById(`ProductSubmitButton-${this.sectionId}`)
              ?.hasAttribute("disabled") ?? true,
            window.variantStrings.soldOut
          );

          html
            .getElementById(`ProductSubmitButton-${this.sectionId}`)
            ?.hasAttribute("disabled")
            ? this.toggleStickyAtcButton(true)
            : this.toggleStickyAtcButton(false);

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      toggleStickyAtcButton(disable = true) {
        const sticky_atc_btn = document.getElementById("stick_add_to_cart");
        const mainBuyNowButton = document.querySelector(
          `#ProductInfo-${this.sectionId} .rheidon-main-buy-now-button`
        );
        if (sticky_atc_btn && disable) {
          sticky_atc_btn.setAttribute("disabled", "disabled");
        } else if (sticky_atc_btn) {
          sticky_atc_btn.removeAttribute("disabled");
        }
        if (mainBuyNowButton && disable) {
          mainBuyNowButton.setAttribute("disabled", "disabled");
        } else if (mainBuyNowButton) {
          mainBuyNowButton.removeAttribute("disabled");
        }
      }

      updateVariantInputs(variantId) {
        const floating_cart_id = document.getElementById("floating_cart_id");
        floating_cart_id?.setAttribute("value", variantId);
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? "";
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        // this.querySelector('share-button')?.updateUrl(
        //   `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        // );

        if (this.dataset.updateUrl === "false") return;
        window.history.replaceState(
          {},
          "",
          `${url}${variantId ? `?variant=${variantId}` : ""}`
        );
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(
          true,
          window.variantStrings.unavailable
        );

        const selectors = [
          "price",
          "Inventory",
          "Sku",
          "Price-Per-Item",
          "Volume-Note",
          "Volume",
          "Quantity-Rules",
        ]
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(", ");
        document
          .querySelectorAll(selectors)
          .forEach(({ classList }) => classList.add("hidden"));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector("media-gallery ul");
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute("data-zoom-on-hover")) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(
            mediaGallerySource.querySelectorAll("li[data-media-id]")
          );
          const sourceSet = new Set(
            mediaGallerySourceItems.map((item) => item.dataset.mediaId)
          );
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [
              item.dataset.mediaId,
              { item, index },
            ])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] =
            refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll("li[data-media-id]")
          );
          const destinationSet = new Set(
            mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId)
          );
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (
              !sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)
            ) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (
              !destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)
            ) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh)
            [mediaGallerySourceItems, sourceSet, sourceMap] =
              refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach(
            (destinationItem, destinationIndex) => {
              const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

              if (sourceData && sourceData.index !== destinationIndex) {
                mediaGallerySource.insertBefore(
                  sourceData.item,
                  mediaGallerySource.querySelector(
                    `li:nth-of-type(${destinationIndex + 1})`
                  )
                );

                // refresh source now that it has been modified
                [mediaGallerySourceItems, sourceSet, sourceMap] =
                  refreshSourceData();
              }
            }
          );
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(
          `.product-media-modal__content`
        );
        const newModalContent = html.querySelector(
          `product-modal .product-media-modal__content`
        );
        if (modalContent && newModalContent)
          modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity
            ? parseInt(this.quantityInput.dataset.cartQuantity)
            : 0,
          min: this.quantityInput.dataset.min
            ? parseInt(this.quantityInput.dataset.min)
            : 1,
          max: this.quantityInput.dataset.max
            ? parseInt(this.quantityInput.dataset.max)
            : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute("max");
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector(
          ".quantity__rules-cart .loading__spinner"
        ).classList.remove("hidden");
        fetch(
          `${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`
        )
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() =>
            this.querySelector(
              ".quantity__rules-cart .loading__spinner"
            ).classList.add("hidden")
          );
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(
          `Quantity-Form-${sectionId}`
        );
        const selectors = [
          ".quantity__input",
          ".quantity__rules",
          ".quantity__label",
        ];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === ".quantity__input") {
            const attributes = [
              "data-cart-quantity",
              "data-min",
              "data-max",
              "step",
            ];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector("variant-selects");
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "related-products"
        );
        return document.querySelector(
          `product-recommendations[data-section-id^="${relatedProductsSectionId}"]`
        );
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "quick_order_list"
        );
        return document.querySelector(
          `quick-order-list[data-id^="${quickOrderListSectionId}"]`
        );
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}

subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
  document.querySelector(".sku").innerText = event.data.variant.sku;
});

(() => {
  const truckIcon =
    '<svg width="49" height="40" viewBox="0 0 49 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><path d="M41.4881 20.1792L36.7044 13.6728C36.6151 13.5532 36.4989 13.4559 36.3651 13.3885C36.2313 13.3211 36.0835 13.2856 35.9335 13.2847H32.4939V8.14898C32.4926 8.02592 32.4669 7.90432 32.4182 7.79112C32.3696 7.67792 32.2989 7.57534 32.2102 7.48924C32.1215 7.40314 32.0167 7.33519 31.9015 7.2893C31.7864 7.2434 31.6633 7.22044 31.5392 7.22173H10.9811C10.7318 7.22068 10.4923 7.31733 10.3145 7.49063C10.1368 7.66394 10.0354 7.89986 10.0322 8.14702L10.0322 11.9497C10.0322 12.4594 10.4592 12.8769 10.983 12.8769C11.2326 12.8785 11.4727 12.7821 11.6508 12.6087C11.829 12.4353 11.9307 12.1991 11.9339 11.9516V9.0723H30.6002V26.0211H22.3177C21.9324 25.5995 21.4626 25.2624 20.9384 25.0312C20.4142 24.7999 19.8472 24.6797 19.2735 24.6782C18.0677 24.6782 16.9824 25.2075 16.2313 26.0211H11.9339V23.0766C11.9307 22.8291 11.829 22.5929 11.6508 22.4195C11.4727 22.2462 11.2326 22.1498 10.983 22.1513C10.7335 22.1498 10.4934 22.2462 10.3152 22.4195C10.1371 22.5929 10.0354 22.8291 10.0322 23.0766V26.9464C10.0322 27.456 10.4592 27.8716 10.983 27.8716H15.2528C15.1959 28.1332 15.1674 28.4 15.1678 28.6675C15.1678 30.871 17.0121 32.6667 19.2656 32.6667C21.5191 32.6667 23.3634 30.871 23.3634 28.6675C23.3634 28.3911 23.3357 28.1304 23.2784 27.8716H31.7882C31.7313 28.1332 31.7028 28.4 31.7032 28.6675C31.7032 30.871 33.5475 32.6667 35.8109 32.6667C38.0743 32.6667 39.9087 30.871 39.9087 28.6675C39.9087 28.3911 39.881 28.1304 39.8237 27.8716H40.7093C40.9585 27.8727 41.1981 27.776 41.3758 27.6027C41.5535 27.4294 41.655 27.1935 41.6581 26.9464V20.7164C41.6633 20.5235 41.6035 20.3345 41.4881 20.1792ZM19.2656 30.8239C18.5342 30.8239 17.8522 30.4691 17.445 29.879C17.2442 29.5898 17.1196 29.2553 17.0823 28.9061C17.045 28.5569 17.0963 28.204 17.2315 27.8795C17.3985 27.4814 17.681 27.1416 18.0432 26.9032C18.4054 26.6648 18.8309 26.5386 19.2656 26.5406C20.161 26.5406 20.9656 27.0699 21.3016 27.8814C21.5784 28.5401 21.4993 29.2909 21.0901 29.881C20.8861 30.1734 20.6134 30.412 20.2955 30.5763C19.9776 30.7406 19.6241 30.8256 19.2656 30.8239ZM35.8109 30.8239C35.2316 30.8286 34.674 30.6053 34.2604 30.2029C33.8469 29.8005 33.6111 29.2519 33.6049 28.6773C33.6147 28.1066 33.8506 27.5625 34.2615 27.1627C34.6724 26.7629 35.2254 26.5394 35.801 26.5406C36.6985 26.5406 37.503 27.0699 37.8371 27.8814C38.0016 28.2697 38.0447 28.698 37.9608 29.1108C37.8769 29.5236 37.6699 29.9019 37.3666 30.1966C36.9521 30.6015 36.3927 30.827 35.8109 30.8239ZM39.7664 26.0309H38.863C38.4777 25.6093 38.0079 25.2722 37.4838 25.041C36.9596 24.8097 36.3925 24.6895 35.8188 24.688C35.1755 24.6892 34.5413 24.8396 33.9671 25.1272C33.3929 25.4148 32.8945 25.8317 32.5117 26.3445V15.1352H35.459L39.7762 21.0124V26.0309H39.7664Z" fill="#111111"/><path d="M16.531 14.9278C17.0113 14.9278 17.4047 14.5083 17.4047 13.9966C17.4047 13.483 17.0113 13.0635 16.531 13.0635H10.4288C9.94841 13.0635 9.55701 13.483 9.55701 13.9966C9.55701 14.5083 9.94841 14.9278 10.4288 14.9278H16.531ZM17.3078 16.9136C17.3078 16.402 16.9164 15.9824 16.4361 15.9824H8.92841C8.44806 15.9824 8.05469 16.402 8.05469 16.9136C8.05469 17.4272 8.44806 17.8467 8.92841 17.8467H16.4361C16.9164 17.8467 17.3078 17.4351 17.3078 16.9136ZM21.0182 20.9049C21.4985 20.9049 21.8919 20.4854 21.8919 19.9718C21.8919 19.4601 21.4985 19.0406 21.0182 19.0406H10.0967C9.61829 19.0406 9.22492 19.4601 9.22492 19.9718C9.22492 20.4854 9.61829 20.9049 10.0967 20.9049H21.0182Z" fill="#111111"/></svg>';

  const getMainProduct = () =>
    document.querySelector('product-info[id^="MainProduct-"]');

  const getText = (selector, fallback = "") =>
    document.querySelector(selector)?.textContent?.trim().replace(/\s+/g, " ") ||
    fallback;

  const parseMoneyText = (text = "") => {
    const source = text.trim();
    const match = source.match(/-?\d[\d.,]*/);
    if (!match) return null;

    const numberText = match[0];
    const commaIndex = numberText.lastIndexOf(",");
    const dotIndex = numberText.lastIndexOf(".");
    const possibleDecimalIndex = Math.max(commaIndex, dotIndex);
    let decimalIndex = -1;

    if (possibleDecimalIndex >= 0) {
      const trailingDigits = numberText.length - possibleDecimalIndex - 1;
      if (trailingDigits > 0 && trailingDigits <= 2) {
        decimalIndex = possibleDecimalIndex;
      }
    }

    const decimalSeparator =
      decimalIndex >= 0 ? numberText[decimalIndex] : "";
    const fractionDigits =
      decimalIndex >= 0 ? numberText.length - decimalIndex - 1 : 0;
    const integerPart =
      decimalIndex >= 0 ? numberText.slice(0, decimalIndex) : numberText;
    const fractionPart =
      decimalIndex >= 0 ? numberText.slice(decimalIndex + 1) : "";
    const integerDigits = integerPart.replace(/[^\d-]/g, "");
    const fractionDigitsText = fractionPart
      .replace(/\D/g, "")
      .padEnd(fractionDigits, "0");
    const scale = 10 ** fractionDigits;
    const sign = integerDigits.startsWith("-") ? -1 : 1;
    const major = Number.parseInt(integerDigits.replace("-", "") || "0", 10);
    const minor = fractionDigits
      ? Number.parseInt(fractionDigitsText || "0", 10)
      : 0;

    return {
      prefix: source.slice(0, match.index),
      suffix: source.slice(match.index + numberText.length),
      unitValue: sign * (major * scale + minor),
      scale,
      decimalSeparator,
      fractionDigits,
      groupSeparator: decimalSeparator === "," ? "." : ",",
    };
  };

  const formatMoneyText = (money, quantity) => {
    if (!money) return "";

    const total = Math.round(money.unitValue * quantity);
    const absoluteTotal = Math.abs(total);
    const whole = Math.floor(absoluteTotal / money.scale);
    const fraction = absoluteTotal % money.scale;
    const wholeText = String(whole).replace(
      /\B(?=(\d{3})+(?!\d))/g,
      money.groupSeparator
    );
    const fractionText = money.fractionDigits
      ? money.decimalSeparator +
        String(fraction).padStart(money.fractionDigits, "0")
      : "";
    const sign = total < 0 ? "-" : "";

    return `${money.prefix}${sign}${wholeText}${fractionText}${money.suffix}`;
  };

  const multiplyMoneyText = (text, quantity) => {
    const money = parseMoneyText(text);
    return money ? formatMoneyText(money, quantity) : text;
  };

  const getPriceData = () => {
    const root = getMainProduct();
    const priceRoot = root?.querySelector('[id^="price-"]');
    const quantity = getQuantityNumber();
    const current =
      priceRoot?.querySelector(".price-item--sale.price-item--last")
        ?.textContent ||
      priceRoot?.querySelector(".price__regular .price-item")
        ?.textContent ||
      "";

    return {
      current: multiplyMoneyText(current.trim().replace(/\s+/g, " "), quantity),
      compare: multiplyMoneyText(
        priceRoot
          ?.querySelector(".rheidon-price-extra__compare")
          ?.textContent?.trim()
          .replace(/\s+/g, " ") || "",
        quantity
      ),
      save: multiplyMoneyText(
        priceRoot
          ?.querySelector(".rheidon-price-extra__save")
          ?.textContent?.trim()
          .replace(/\s+/g, " ") || "",
        quantity
      ),
    };
  };

  const getVariantId = () => {
    const root = getMainProduct();
    return (
      root?.querySelector('form[action*="/cart/add"] input[name="id"]:not([disabled])')
        ?.value ||
      root?.querySelector('form[action*="/cart/add"] input[name="id"]')?.value ||
      ""
    );
  };

  const getQuantity = () => {
    const root = getMainProduct();
    return (
      root?.querySelector('quantity-input input[name="quantity"]')?.value ||
      root?.querySelector('input[name="quantity"]')?.value ||
      "1"
    );
  };

  const getQuantityNumber = () => {
    const quantity = Number.parseInt(getQuantity(), 10);
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  };

  const getMainProductForm = () =>
    getMainProduct()?.querySelector('form[action*="/cart/add"]');

  const getMainAddButton = () => {
    const root = getMainProduct();
    return root?.querySelector(`#ProductSubmitButton-${root.dataset.section}`);
  };

  const submitCurrentProductToCheckout = () => {
    const form = getMainProductForm();
    const variantId = getVariantId();
    if (!form || !variantId) return;

    const variantInput = form.querySelector('input[name="id"]');
    if (variantInput) {
      variantInput.disabled = false;
      variantInput.value = variantId;
    }

    let returnTo = form.querySelector('input[name="return_to"]');
    if (!returnTo) {
      returnTo = document.createElement("input");
      returnTo.type = "hidden";
      returnTo.name = "return_to";
      form.appendChild(returnTo);
    }
    returnTo.value = "/checkout";
    form.action = window.routes?.cart_add_url || "/cart/add";
    HTMLFormElement.prototype.submit.call(form);
  };

  const getSourceButton = () =>
    document.querySelector(
      "#sticky-cart-portal .essential-preorder-extra-add-to-cart-button"
    );

  const decorateStickyCart = () => {
    const root = getMainProduct();
    const portal = document.querySelector("#sticky-cart-portal");
    const sourceButton = getSourceButton();
    if (!root || !portal || !sourceButton) return;

    portal.querySelectorAll("div").forEach((node) => {
      if (node.classList.contains("rheidon-sticky-cart")) return;
      if (window.getComputedStyle(node).position === "fixed") {
        node.classList.add("rheidon-sticky-cart__source");
      }
    });

    let bar = portal.querySelector(".rheidon-sticky-cart");
    if (!bar) {
      bar = document.createElement("div");
      bar.className = "rheidon-sticky-cart";
      bar.innerHTML = `
        <div class="rheidon-sticky-cart__inner">
          <div class="rheidon-sticky-cart__delivery">
            ${truckIcon}
            <span class="rheidon-sticky-cart__delivery-text"></span>
          </div>
          <div class="rheidon-sticky-cart__price">
            <div class="rheidon-sticky-cart__price-row">
              <span class="rheidon-sticky-cart__current"></span>
              <s class="rheidon-sticky-cart__compare"></s>
            </div>
            <span class="rheidon-sticky-cart__save"></span>
          </div>
          <div class="rheidon-sticky-cart__actions">
            <button class="rheidon-sticky-cart__add" type="button"></button>
            <div class="rheidon-sticky-cart__buy-form">
              <button class="rheidon-sticky-cart__buy" type="button"></button>
            </div>
          </div>
        </div>
      `;
      portal.appendChild(bar);
      bar
        .querySelector(".rheidon-sticky-cart__add")
        .addEventListener("click", () => getMainAddButton()?.click());
      bar
        .querySelector(".rheidon-sticky-cart__buy")
        .addEventListener("click", submitCurrentProductToCheckout);
      if (typeof ResizeObserver !== "undefined") {
        const sizeObserver = new ResizeObserver(() => {
          document.documentElement.style.setProperty(
            "--rheidon-sticky-cart-mobile-padding",
            `${Math.ceil(bar.getBoundingClientRect().height) + 10}px`
          );
        });
        sizeObserver.observe(bar);
      }
    }

    const price = getPriceData();
    const mainAddButton = getMainAddButton();
    const addLabel =
      mainAddButton?.textContent?.trim().replace(/\s+/g, " ") ||
      sourceButton.textContent?.trim().replace(/\s+/g, " ") ||
      getText("#ProductSubmitButton-" + root.dataset.section, "Add to cart");
    const buyLabel = root.dataset.rheidonBuyNowLabel || "Buy Now";
    const delivery =
      root.dataset.rheidonStickyDelivery ||
      "In Stock—Estimated delivery in 3 - 5 business days.";
    const disabled =
      !mainAddButton ||
      mainAddButton.disabled ||
      mainAddButton.getAttribute("aria-disabled") === "true";

    const mainButtons = mainAddButton?.closest(".product-form__buttons");
    if (mainButtons) {
      let mainDelivery = root.querySelector(".rheidon-main-delivery");
      if (!mainDelivery) {
        mainDelivery = document.createElement("div");
        mainDelivery.className = "rheidon-main-delivery";
        mainDelivery.innerHTML = `${truckIcon}<span></span>`;
        mainButtons.insertAdjacentElement("afterend", mainDelivery);
      }
      const deliveryText = mainDelivery.querySelector("span");
      if (deliveryText.textContent !== delivery) {
        deliveryText.textContent = delivery;
      }
      mainDelivery.hidden = disabled || delivery.trim() === "";
    }

    bar.querySelector(".rheidon-sticky-cart__delivery-text").textContent =
      delivery;
    bar.querySelector(".rheidon-sticky-cart__current").textContent =
      price.current;
    bar.querySelector(".rheidon-sticky-cart__compare").textContent =
      price.compare;
    bar.querySelector(".rheidon-sticky-cart__compare").hidden =
      price.compare === "";
    bar.querySelector(".rheidon-sticky-cart__save").textContent = price.save;
    bar.querySelector(".rheidon-sticky-cart__save").hidden = price.save === "";
    bar.querySelector(".rheidon-sticky-cart__add").textContent = addLabel;
    bar.querySelector(".rheidon-sticky-cart__buy").textContent = buyLabel;
    bar.querySelector(".rheidon-sticky-cart__add").disabled = disabled;
    bar.querySelector(".rheidon-sticky-cart__buy").disabled = disabled;
    document.documentElement.classList.add("rheidon-sticky-cart-ready");
  };

  let frame = 0;
  const scheduleDecorateStickyCart = () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(decorateStickyCart);
  };

  document.addEventListener("DOMContentLoaded", scheduleDecorateStickyCart);
  document.addEventListener("click", (event) => {
    if (event.target.closest(".rheidon-main-buy-now-button")) {
      event.preventDefault();
      submitCurrentProductToCheckout();
      return;
    }

    if (event.target.closest('product-info[id^="MainProduct-"] .quantity__button')) {
      window.setTimeout(scheduleDecorateStickyCart, 0);
    }
  });
  document.addEventListener("input", (event) => {
    if (event.target.matches('product-info[id^="MainProduct-"] input[name="quantity"]')) {
      scheduleDecorateStickyCart();
    }
  });
  document.addEventListener("change", scheduleDecorateStickyCart);
  subscribe(PUB_SUB_EVENTS.variantChange, scheduleDecorateStickyCart);
  new MutationObserver(scheduleDecorateStickyCart).observe(document.body, {
    childList: true,
    subtree: true,
  });
  scheduleDecorateStickyCart();
})();
